import React, { useState, useMemo } from 'react';
import ToolLayout from './ToolLayout';

const TIERS = [
  {
    id: 'short',
    name: 'Short Film Agreement',
    budgetMax: 50000,
    budgetLabel: 'Under $50,000',
    dayRate: 'Negotiable / Deferred',
    weekRate: 'N/A',
    accentClass: 'border-zinc-600 text-zinc-300',
    highlightClass: 'border-zinc-400 bg-zinc-900',
    tagClass: 'text-zinc-400 border-zinc-700',
    provisions: [
      'Cast may work fully deferred or for minimal flat fees',
      'Simplified paperwork and reporting requirements',
      'Suitable for festival and non-commercial use',
      'Good for building cast relationships early',
    ],
    bestFor: 'Student films, proof-of-concept shorts, spec work',
  },
  {
    id: 'ultra',
    name: 'Ultra Low Budget Agreement',
    budgetMax: 250000,
    budgetLabel: 'Up to $250,000',
    dayRate: '~$206 / day',
    weekRate: '~$933 / week',
    accentClass: 'border-sky-800 text-sky-300',
    highlightClass: 'border-sky-500 bg-sky-500/5',
    tagClass: 'text-sky-400 border-sky-800',
    provisions: [
      'Significantly simplified terms vs. Basic Agreement',
      'Actors may receive points / backend participation',
      'Some geographic and platform restrictions apply',
      'Screen credit and billing requirements',
    ],
    bestFor: 'Debut features, micro-budget genre films',
  },
  {
    id: 'modified',
    name: 'Modified Low Budget Agreement',
    budgetMax: 700000,
    budgetLabel: '$250,001 – $700,000',
    dayRate: '~$335 / day',
    weekRate: '~$1,166 / week',
    accentClass: 'border-blue-800 text-blue-300',
    highlightClass: 'border-blue-500 bg-blue-500/5',
    tagClass: 'text-blue-400 border-blue-800',
    provisions: [
      'Full SAG-AFTRA member protections',
      'More flexible terms than Basic Agreement',
      'Standard overtime, turnaround, and residual rules apply',
      'Most common agreement for quality indie features',
    ],
    bestFor: 'Quality indie features targeting film festivals',
  },
  {
    id: 'low',
    name: 'Low Budget Agreement',
    budgetMax: 2500000,
    budgetLabel: '$700,001 – $2,500,000',
    dayRate: 'Full scale rates',
    weekRate: '~$1,400 – $2,000 / week',
    accentClass: 'border-indigo-800 text-indigo-300',
    highlightClass: 'border-indigo-500 bg-indigo-500/5',
    tagClass: 'text-indigo-400 border-indigo-800',
    provisions: [
      'Full scale rates and member benefits',
      'Access to all SAG-AFTRA members globally',
      'Standard residuals structure applies',
      'Strong for prestige indie and limited series',
    ],
    bestFor: 'Prestige indie features, TV pilots, limited series',
  },
  {
    id: 'basic',
    name: 'Basic Agreement (Theatrical)',
    budgetMax: Infinity,
    budgetLabel: 'Over $2,500,000',
    dayRate: '~$1,100+ / day',
    weekRate: '~$3,800+ / week',
    accentClass: 'border-violet-800 text-violet-300',
    highlightClass: 'border-violet-500 bg-violet-500/5',
    tagClass: 'text-violet-400 border-violet-800',
    provisions: [
      'Full scale rates, union benefits, and H&W contributions',
      'Complete residuals on all platforms and markets',
      'Standard for studio and high-end independent productions',
      'Pension, health, and retirement contributions required',
    ],
    bestFor: 'Studio productions, studio-level independent films',
  },
];

function fmt(n) {
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + n;
}

export default function SAGTierFinder() {
  const [budget, setBudget] = useState(500000);

  const activeTier = useMemo(
    () => TIERS.find((t) => budget <= t.budgetMax),
    [budget]
  );

  return (
    <ToolLayout
      accent="Aperture Slate // Labor Tools"
      title="SAG-AFTRA"
      titleAccent="Tier Finder"
      description="Enter your total production budget and instantly see which SAG-AFTRA agreement applies — with minimum rates and key provisions in plain English."
    >
      <div className="space-y-6">
        {/* Budget input */}
        <div className="border border-zinc-800 bg-zinc-950 p-8">
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Total Production Budget</label>
            <span className="text-2xl font-black tabular-nums">{fmt(budget)}</span>
          </div>
          <input
            type="range" min={0} max={5000000} step={10000} value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>$0</span><span>$5M+</span>
          </div>
        </div>

        {/* Active tier callout */}
        {activeTier && (
          <div className={`border-2 p-6 ${activeTier.highlightClass}`}>
            <div className="flex items-center justify-between mb-4">
              <span className={`text-[10px] font-bold tracking-widest border px-2 py-0.5 ${activeTier.tagClass}`}>
                YOUR AGREEMENT
              </span>
              <span className="text-[11px] text-zinc-500 tracking-wider">{activeTier.budgetLabel}</span>
            </div>
            <h2 className="text-2xl font-black tracking-tight uppercase mb-5">{activeTier.name}</h2>
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-1">Min Day Rate</p>
                <p className="text-lg font-black text-white">{activeTier.dayRate}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-1">Min Week Rate</p>
                <p className="text-lg font-black text-white">{activeTier.weekRate}</p>
              </div>
            </div>
            <div className="space-y-2 mb-5">
              {activeTier.provisions.map((p, i) => (
                <div key={i} className="flex gap-2 text-xs text-zinc-300 leading-relaxed">
                  <span className="text-sky-500 shrink-0 mt-0.5">—</span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-800 pt-4">
              <span className="text-[10px] text-zinc-500 tracking-widest uppercase">Best for: </span>
              <span className="text-[10px] text-zinc-300">{activeTier.bestFor}</span>
            </div>
          </div>
        )}

        {/* All tiers reference */}
        <div>
          <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-4">All Agreements at a Glance</p>
          <div className="space-y-2">
            {TIERS.map((t) => {
              const isActive = t.id === activeTier?.id;
              return (
                <div
                  key={t.id}
                  className={`border p-4 flex justify-between items-center transition-all duration-200 ${
                    isActive ? t.highlightClass : 'border-zinc-900 opacity-40'
                  }`}
                >
                  <div>
                    <span className={`text-[11px] font-bold tracking-wide ${isActive ? 'text-white' : 'text-zinc-500'}`}>
                      {t.name}
                    </span>
                    <span className="text-[10px] text-zinc-600 ml-2">{t.budgetLabel}</span>
                  </div>
                  <span className="text-[11px] text-zinc-400 tabular-nums">{t.dayRate}</span>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-[10px] text-zinc-600 leading-relaxed tracking-wide border-t border-zinc-900 pt-4">
          Rates are approximate and based on publicly available SAG-AFTRA schedules. Rates change periodically. Always verify current minimum rates directly with SAG-AFTRA (sagaftra.org) and consult an entertainment labor attorney before executing any cast agreement.
        </p>
      </div>
    </ToolLayout>
  );
}
